/**
 * Fully custom dot navigation for review-slideshow, replacing the native
 * slideshow-controls dots.
 *
 * The native slideshow-component tracks the active slide, and the native
 * dots navigate to a slide, both via elements carrying ref="slides[]"
 * (rendered by the slideshow-slide snippet). review-slideshow's cards
 * come from a merchant-editable block list rendered via
 * content_for 'blocks', which can't be wrapped per-item server-side, so
 * those ref markers never get attached - the native dots stayed on the
 * first slide and clicking one did nothing. This drives both directions
 * (scroll -> active dot, dot click -> scroll) directly instead.
 */

/**
 * Each slide is set to 100% of the scroller's own width (flex: 0 0 100%),
 * so the scroller's own client width is always the reliable source of
 * truth for one slide's width - reading it off the first child directly
 * is fragile (it can report 0 before the browser has settled layout).
 * @param {Element} scroller
 * @returns {number}
 */
function getSlideWidth(scroller) {
  return scroller.clientWidth;
}

/**
 * @param {NodeListOf<Element>} dots
 * @param {number} index
 */
function setActiveDot(dots, index) {
  dots.forEach((dot, i) => {
    const isActive = i === index;
    dot.classList.toggle('is-active', isActive);
    dot.setAttribute('aria-selected', String(isActive));
  });
}

/**
 * @param {Element} review_slideshow
 */
function init_instance(review_slideshow) {
  if (review_slideshow.hasAttribute('data-dots-wired')) return;
  review_slideshow.setAttribute('data-dots-wired', 'true');

  const scroller = review_slideshow.querySelector('slideshow-slides');
  const dots = review_slideshow.querySelectorAll('[data-review-dot]');
  if (!scroller || dots.length === 0) return;

  let ticking = false;

  scroller.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;

    requestAnimationFrame(() => {
      ticking = false;

      const width = getSlideWidth(scroller);
      if (!width) return;

      const index = Math.round(scroller.scrollLeft / width);
      setActiveDot(dots, index);
    });
  });

  dots.forEach((dot, index) => {
    dot.addEventListener('click', () => {
      const width = getSlideWidth(scroller);
      if (!width) return;

      scroller.scrollTo({ left: width * index, behavior: 'smooth' });
      setActiveDot(dots, index);
    });
  });
}

/**
 * @param {ParentNode} root
 */
function init(root) {
  root.querySelectorAll('.review-slideshow').forEach((review_slideshow) => init_instance(review_slideshow));
}

init(document);

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof ParentNode) init(event.target);
});
